import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { isFalsy, isTruthy } from 'lib/Helpers';
import { DownArrow } from 'components/CommonIcons';

import Searchbar from '../Searchbar';
import WithClickOutside from '../WithClickOutside';
import CategorizedListSection from './CategorizedListSection';
import * as styles from './CategorizedList.scss';

/**
 * @class CategorizedList
 * @classdesc Reusable CategorizedList Component
 * @property {Object} state
 */
export default class CategorizedList extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(
      PropTypes.shape({
        className: PropTypes.string,
        title: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            className: PropTypes.string,
            dataKey: PropTypes.string,
            displayValue: PropTypes.string,
            value: PropTypes.any,
            style: PropTypes.objectOf(
              PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string,
              ]),
            ),
          }),
        ),
        style: PropTypes.objectOf(
          PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
          ]),
        ),
      }),
    ).isRequired,
    displayValue: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    onClick: PropTypes.func.isRequired,
    arrowClassName: PropTypes.string,
    className: PropTypes.string,
    hasSearch: PropTypes.bool,
    headerClassName: PropTypes.string,
    searchCallback: PropTypes.func,
    searchbarSize: PropTypes.string,
    size: PropTypes.string,
    style: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
    ),
  };

  static defaultProps = {
    arrowClassName: null,
    className: null,
    headerClassName: null,
    hasSearch: true,
    searchCallback: null,
    searchbarSize: 'SMALL',
    size: 'SMALL',
    style: null,
  };

  constructor(props) {
    super(props);
    const { displayValue } = this.props;

    this.state = {
      displayValue,
      open: false,
      searchValue: '',
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.displayValue !== prevState.displayValue) return { displayValue: nextProps.displayValue };
    return null;
  }

  /**
   * Closes the List on ClickOutside Events
   * @return {Function} this.setState
   */
  handleClickOutside = () => {
    return this.setState({ open: false, searchValue: '' });
  }

  /**
   * List Visibility Toggler
   * @return {Function} this.setState
   */
  handleToggleListContent = () => {
    return this.setState(prevState => ({ open: !prevState.open, searchValue: '' }));
  }

  /**
   * Attribute Search Handler
   * @param {string} searchValue
   * @return {Function} this.setState
   */
  handleSearch = searchValue => {
    const { searchCallback } = this.props;

    this.setState({ searchValue });
    return isTruthy(searchCallback)
      ? searchCallback(searchValue)
      : null;
  }

  /**
   * Attribute Selection Handler
   * @param {string} displayValue
   * @param {*} selection
   * @return {Function} props.onClick
   */
  handleSelection = selection => {
    const { onClick } = this.props;

    this.setState({ open: false, searchValue: '' });
    return onClick(selection);
  }

  /**
   * Filters Data List Based on Current SearchValue in State
   * @param {Array.<Object>} data
   * @return {Array.<Object>}
   */
  handleDataFiltering = data => {
    const { searchValue } = this.state;
    /* Return data back out if no search criteria initiated */
    if (searchValue === '') return data;

    return data.reduce((acc, section) => {
      const matches = section.options.filter(option => {
        const { displayValue } = option;
        /* Lowercase inputs and searchValue for loose comparisons */
        return displayValue.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
      });
      const newSection = Object.assign({}, section, { options: [...matches] });
      /* We don't want to see empty categories */
      if (matches.length > 0) acc.push(newSection);
      return acc;
    }, []);
  }

  renderListHeader = () => {
    const { arrowClassName, headerClassName, size } = this.props;
    const { displayValue, open } = this.state;
    const isLarge = size === 'LARGE';

    return (
      <div
        className={classNames(
          styles.listHeader,
          { [styles.listHeaderLarge]: isLarge },
          { [styles.listHeaderOpen]: open },
          headerClassName,
        )}
        onClick={this.handleToggleListContent}
      >
        <p className={styles.displayValue} title={displayValue}>{displayValue}</p>
        <div className={classNames(
          styles.arrow,
          { [styles.arrowLarge]: isLarge },
          arrowClassName,
        )}>
          <DownArrow />
        </div>
      </div>
    );
  }

  renderListContent = () => {
    const { data } = this.props;
    const { searchValue } = this.state;

    if (isFalsy(data)) return null;

    const result = this.handleDataFiltering(data);
    return (
      <div className={styles.listContent}>
        {this.renderUserSearch()}
        <div className={styles.sections}>
          {result.map(section => {
            return (
              <CategorizedListSection
                className={section.className}
                key={section.title}
                onClick={this.handleSelection}
                options={section.options}
                title={section.title}
                searchValue={searchValue}
                style={section.style}
              />
            );
          })}
        </div>
      </div>
    );
  }

  renderUserSearch = () => {
    const { hasSearch, searchbarSize } = this.props;
    const { searchValue } = this.state;

    if (isFalsy(hasSearch)) return null;
    return (
      <div className={styles.searchSection}>
        <Searchbar
          inputClassName={styles.attributeSearch}
          onChange={this.handleSearch}
          placeholder="Find an Attribute"
          size={searchbarSize}
          value={searchValue}
        />
      </div>
    );
  }

  render() {
    const { className, style } = this.props;
    const { open } = this.state;

    return (
      <WithClickOutside handleClickOutside={this.handleClickOutside}>
        <div className={classNames(styles.categorizedList, className)} style={style}>
          {this.renderListHeader()}
          {open && this.renderListContent()}
        </div>
      </WithClickOutside>
    );
  }
}
